import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Expand } from "lucide-react";
import Reveal from "@/components/site/Reveal";

const FILTERS = ["All", "Cleanout", "Debris", "Demolition"];

const JOBS = [
  {
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_haul-it-away-16/artifacts/ctlcmssn_20260911_231001-COLLAGE.webp",
    alt: "Before and after of a garage cleanout by GW Junk Solutions",
    cat: "Cleanout",
    title: "Garage Cleanout, Swept Clean",
  },
  {
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_haul-it-away-16/artifacts/6cv5xprm_20260911_171848-COLLAGE.webp",
    alt: "Before and after of a backyard junk pile cleanup",
    cat: "Debris",
    title: "Backyard Junk Pile, Gone",
  },
  {
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_haul-it-away-16/artifacts/y6tt08u8_20260911_231449-COLLAGE.webp",
    alt: "Before and after of a shed demolition and removal by the pond",
    cat: "Demolition",
    title: "Shed Demolition by the Pond",
  },
  {
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_haul-it-away-16/artifacts/5uderh5z_20260911_172157-COLLAGE.webp",
    alt: "Trailer loaded with debris and a cleaned-out shed interior",
    cat: "Cleanout",
    title: "Shed Cleanout & Haul-Off",
  },
  {
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_haul-it-away-16/artifacts/z86k44ov_20260911_172905-COLLAGE.webp",
    alt: "Before and after of a fallen tree removal and yard cleanup",
    cat: "Debris",
    title: "Fallen Tree Removal",
  },
];

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const visible = filter === "All" ? JOBS : JOBS.filter((j) => j.cat === filter);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36"
    >
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="font-mono-ind text-xs uppercase tracking-[0.3em] text-[#FF9F00]">
            Real Jobs · Before & After
          </p>
          <h2 className="mt-4 font-display text-5xl leading-none tracking-tight sm:text-7xl">
            JOBS WE'VE <span className="text-stroke-orange">HAULED</span>
          </h2>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`gallery-filter-${f.toLowerCase()}`}
              onClick={() => setFilter(f)}
              className={`border px-4 py-2 font-mono-ind text-xs uppercase tracking-widest transition-all duration-300 ${
                filter === f
                  ? "border-[#FF5A00] bg-[#FF5A00] text-[#0B0C0E]"
                  : "border-[#282C34] text-zinc-400 hover:border-[#FF5A00]/60 hover:text-[#FF9F00]"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </Reveal>

      <motion.div layout className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <AnimatePresence mode="popLayout">
          {visible.map((job, i) => (
            <motion.button
              layout
              key={job.src}
              data-testid={`gallery-item-${i}`}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => setLightbox(job)}
              className="group relative aspect-[4/3] overflow-hidden border border-[#282C34] bg-[#14171C] text-left"
            >
              <img
                src={job.src}
                alt={job.alt}
                loading="lazy"
                className="h-full w-full bg-[#0B0C0E] object-contain transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E]/90 via-transparent to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />
              <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-5">
                <div>
                  <p className="font-mono-ind text-[10px] uppercase tracking-[0.25em] text-[#FF9F00]">
                    {job.cat}
                  </p>
                  <p className="mt-1 font-display text-xl tracking-wide text-zinc-100">
                    {job.title}
                  </p>
                </div>
                <Expand className="h-5 w-5 text-zinc-400 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              </div>
            </motion.button>
          ))}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-[#0B0C0E]/95 p-5 backdrop-blur-sm"
          >
            <motion.figure
              initial={{ scale: 0.92, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 24 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-h-[85vh] max-w-5xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={lightbox.src}
                alt={lightbox.alt}
                className="max-h-[78vh] w-full border border-[#282C34] object-contain"
              />
              <figcaption className="mt-4 flex items-center justify-between">
                <span className="font-display text-2xl tracking-wide">{lightbox.title}</span>
                <button
                  data-testid="gallery-lightbox-close"
                  onClick={() => setLightbox(null)}
                  className="flex h-10 w-10 items-center justify-center border border-[#282C34] text-zinc-300 transition-colors hover:border-[#FF5A00] hover:text-[#FF9F00]"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
